import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import "../styles/legal.css";
import logo from "../assets/PetfamLogo.png";
import { X } from "lucide-react";

export default function Reembolsos() {
  const navigate = useNavigate();
  const location = useLocation();

  // =========================================================
  // 🔀 Esta página se abre desde dos lugares:
  // - Desde el HOME (sin estado): se ve igual que siempre, con el botón
  //   "Suscribirse", y la X lleva al inicio.
  // - Desde la INTERFAZ (panel "Edita tu perfil" → Nosotros), que la abre
  //   con state { from: "interfaz" }: se oculta "Suscribirse" y la X (y el
  //   logo) vuelven a la interfaz con el panel de perfil abierto en
  //   "Nosotros".
  // =========================================================
  const fromInterfaz = location.state?.from === "interfaz";

  const handleClose = () => {
    if (fromInterfaz) {
      navigate("/interfaz", { replace: true, state: { reopenProfilePanel: "about" } });
    } else {
      navigate("/");
    }
  };

  // Sistema de animaciones de entrada
  const t = location.state?.transition;
  const screenUp = t === "screen" || t === "screen-up" || t === "up";
  const screenDown = t === "screen-down" || t === "down";

  const [enterClass, setEnterClass] = useState(
    screenDown ? "is-entering-down" : screenUp ? "is-entering" : ""
  );

  useEffect(() => {
    if (screenUp || screenDown) {
      const id = setTimeout(() => setEnterClass(""), 420);
      return () => clearTimeout(id);
    }
  }, [screenUp, screenDown]);

  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  const lastUpdated = "7 de noviembre de 2025";

  return (
    <div className={`legal-shell ${enterClass}`}>
      {/* ✕ para volver (al inicio o a la interfaz, según desde dónde se abrió).
          Los estilos van aquí y no en legal.css para que el botón se vea sí o
          sí: antes quedaba invisible o tapado por el header. */}
      <button
        className="close-btn legal-close-fixed"
        onClick={handleClose}
        aria-label={fromInterfaz ? "Volver a tu perfil" : "Volver al inicio"}
        title={fromInterfaz ? "Volver a tu perfil" : "Volver al inicio"}
        style={{
          position: "fixed",
          top: "max(16px, env(safe-area-inset-top))",
          right: "16px",
          zIndex: 9999,
          width: 44,
          height: 44,
          borderRadius: "50%",
          border: "none",
          background: "#FFFFFF",
          color: "#0B2F3E",
          display: "grid",
          placeItems: "center",
          cursor: "pointer",
          boxShadow: "0 8px 22px rgba(11,47,62,.18)",
          padding: 0,
        }}
      >
        <X size={20} strokeWidth={2.2} />
      </button>

      {/* header */}
      <header className="legal-header">
        <div className="legal-header-inner">
          {fromInterfaz ? (
            <button
              type="button"
              className="legal-logo"
              onClick={handleClose}
              aria-label="Volver a tu perfil"
              style={{ border: "none", background: "transparent", padding: 0, cursor: "pointer" }}
            >
              <img src={logo} alt="Petfam logo" />
            </button>
          ) : (
            <Link to="/" className="legal-logo" aria-label="Ir al inicio">
              <img src={logo} alt="Petfam logo" />
            </Link>
          )}

          {/* "Suscribirse" solo tiene sentido en el Home, no para quien ya es usuario */}
          {!fromInterfaz && (
            <Link to="/crear-cuenta" className="legal-cta">
              Suscribirse
            </Link>
          )}
        </div>
      </header>

      {/* contenido */}
      <main className="legal-main" role="main">
        <section className="legal-hero">
          <h1 className="legal-title">Política de reembolsos y cancelaciones</h1>
          <p className="legal-updated">Última actualización: {lastUpdated}</p>

          <div className="legal-content">
            <h3>1. Naturaleza del servicio</h3>
            <p>
              PETFAM funciona bajo un modelo de suscripción mensual recurrente, que da acceso a consultas
              virtuales con profesionales veterinarios o etólogos, además de herramientas digitales de gestión
              (historia clínica y calendario).
            </p>

            <h3>2. Reembolsos</h3>
            <p>
              Dado que el servicio está disponible de forma continua durante el periodo de suscripción, no se
              realizan reembolsos por uso parcial, cancelación anticipada o falta de utilización por parte del usuario.
              Solo se contemplarán reembolsos excepcionales cuando exista un error comprobado en el cobro o
              una falla técnica atribuible a PETFAM que impida completamente el acceso al servicio.
            </p>

            <h3>3. Cancelación de la suscripción</h3>
            <p>
              El usuario puede cancelar su suscripción en cualquier momento desde su cuenta. La cancelación
              evitará el cobro del siguiente ciclo de facturación, pero no implica reembolso del periodo ya pagado.
            </p>

            <h3>4. Casos excepcionales</h3>
            <p>
              En caso de cobro duplicado o error en el sistema de pago, el usuario podrá solicitar revisión
              escribiendo a soporte@petfam.club dentro de los 5 días hábiles siguientes al cobro.
            </p>

            <h3>5. Pasarela de pagos</h3>
            <p>
              Los pagos son procesados a través de un tercero (Wompi).
              PETFAM no almacena datos financieros del usuario. Cualquier error en la transacción deberá ser
              gestionado inicialmente ante la pasarela correspondiente.
            </p>

            <div className="legal-footnote">
              PETFAM podrá actualizar estas políticas cuando sea necesario. Revise
              periódicamente esta página para conocer la versión vigente.
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}